import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  FileDownload as DownloadIcon,
  FileUpload as UploadIcon,
  Person as PersonIcon,
  Inventory as InventoryIcon,
  EditNote as EditNoteIcon,
  Link as LinkIcon,
  Inventory2 as StockIcon,
  LocalShipping as DeliveredIcon,
  AssignmentReturn as ReturnedIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";

/* ---------- helpers (moved here from the page) ---------- */
const EXAMPLES = {
  "Name": "John Doe",
  "Customer Name": "John Doe",
  "Contact Number": "9876543210",
  "Email": "john@example.com",
  "Address": "123 Main St, City",
  "Opening Balance": "5000",
  "Product Name": "Oxygen Cylinder",
  "Type": "Big / Small",
  "Opening Qty": "10",
  "Delivered Qty": "10",
  "Received Qty": "0",
  "GST Number": "27ABCDE1234F1Z5",
  "GSTIN": "27ABCDE1234F1Z5",
  "State": "Maharashtra",
  "City": "Mumbai",
  "Pincode": "400001",
};

const HINTS = {
  "Name": "Full name of the customer",
  "Customer Name": "Same name as in Sheet 1",
  "Contact Number": "10-digit mobile. Links both sheets",
  "Email": "Used for sending statements",
  "Address": "Delivery / billing address",
  "Opening Balance": "Money owed at start (₹)",
  "Product Name": "Exactly as in your product list",
  "Type": "Write Big or Small",
  "Opening Qty": "Cylinders already with customer",
  "Delivered Qty": "Cylinders you gave them",
  "Received Qty": "Cylinders they returned",
};

const inferType = (name) => {
  const n = String(name).toLowerCase();
  if (n.includes("contact")) return "Text";
  return /(qty|quantity|balance|amount|price|rate|total)/.test(n) ? "Number" : "Text";
};

const buildRows = (headers, required) =>
  (headers || []).map((name) => ({
    name,
    type: inferType(name),
    required: (required || []).includes(name),
    example: EXAMPLES[name] || "—",
    hint: HINTS[name] || "",
  }));

/* ---------- component ---------- */
export default function TemplatePreviewModal({
  open,
  onClose,
  loading,
  templateHeaders = {},
  onDownload,
  onImport,
  isDark = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = useState(0);

  // design tokens
  const c = {
    bg: isDark ? "#0b1220" : "#ffffff",
    surface: isDark ? "#111a2e" : "#F8FAFC",
    card: isDark ? "#162036" : "#ffffff",
    border: isDark ? "#233049" : "#E8EDF3",
    text: isDark ? "#f1f5f9" : "#0F172A",
    sub: isDark ? "#94a3b8" : "#64748B",
    brand: "#7C3AED",
    brandSoft: isDark ? "rgba(124,58,237,0.18)" : "#F3E8FF",
    reqBg: isDark ? "rgba(239,68,68,0.16)" : "#FEF2F2",
    reqText: isDark ? "#fca5a5" : "#DC2626",
    optBg: isDark ? "rgba(148,163,184,0.14)" : "#F1F5F9",
    optText: isDark ? "#cbd5e1" : "#64748B",
  };

  const sheets = [
    {
      key: "customer",
      tab: "Customers",
      num: 1,
      title: "Customer details",
      note: "One row per customer",
      icon: <PersonIcon sx={{ fontSize: 18 }} />,
      accent: "#4F46E5",
      accentSoft: isDark ? "rgba(79,70,229,0.2)" : "#EEF2FF",
      rows: buildRows(
        templateHeaders.customerHeaders?.length
          ? templateHeaders.customerHeaders
          : ["Name", "Contact Number", "Email", "Address", "Opening Balance"],
        templateHeaders.customerRequiredHeaders || ["Name", "Contact Number"]
      ),
    },
    {
      key: "product",
      tab: "Product Balances",
      num: 2,
      title: "Product opening balances",
      note: "Many rows per customer, one per product",
      icon: <InventoryIcon sx={{ fontSize: 18 }} />,
      accent: "#D97706",
      accentSoft: isDark ? "rgba(217,119,6,0.2)" : "#FEF3C7",
      rows: buildRows(
        templateHeaders.productHeaders?.length
          ? templateHeaders.productHeaders
          : ["Customer Name", "Contact Number", "Product Name", "Type", "Opening Qty", "Delivered Qty", "Received Qty"],
        templateHeaders.productRequiredHeaders || ["Customer Name", "Contact Number", "Product Name", "Type"]
      ),
    },
  ];
  const sheet = sheets[tab];

  const Pill = ({ required }) => (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: 20,
        px: 0.9,
        borderRadius: "999px",
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: 0.2,
        background: required ? c.reqBg : c.optBg,
        color: required ? c.reqText : c.optText,
      }}
    >
      {required ? "Required" : "Optional"}
    </Box>
  );

  const steps = [
    { icon: <DownloadIcon sx={{ fontSize: 18 }} />, t: "Download", d: "Get the Excel file" },
    { icon: <EditNoteIcon sx={{ fontSize: 18 }} />, t: "Fill both sheets", d: "Follow the columns below" },
    { icon: <UploadIcon sx={{ fontSize: 18 }} />, t: "Import", d: "Use the Import button" },
  ];

  const qtyCards = [
    { name: "Opening Qty", meaning: "Cylinders already with the customer", icon: <StockIcon sx={{ fontSize: 18 }} />, color: "#4F46E5" },
    { name: "Delivered Qty", meaning: "Cylinders you gave them", icon: <DeliveredIcon sx={{ fontSize: 18 }} />, color: "#D97706" },
    { name: "Received Qty", meaning: "Cylinders they returned", icon: <ReturnedIcon sx={{ fontSize: 18 }} />, color: "#16A34A" },
  ].filter((q) => sheets[1].rows.some((r) => r.name === q.name));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 1400 }}
      PaperProps={{
        sx: {
          width: isMobile ? "96vw" : 820,
          maxWidth: "96vw",
          maxHeight: isMobile ? "94vh" : "90vh",
          m: isMobile ? "8px" : "auto",
          borderRadius: isMobile ? "16px" : "20px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          background: c.bg,
          border: `1px solid ${c.border}`,
          boxShadow: "0 30px 80px -20px rgba(15,23,42,0.45)",
        },
      }}
    >
      {/* ===== Header ===== */}
      <Box
        sx={{
          position: "relative",
          px: isMobile ? 2 : 3,
          py: isMobile ? 2 : 2.5,
          minHeight: isMobile ? 76 : 78,
          display: "flex",
          alignItems: "center",
          color: "#fff",
          background: "linear-gradient(135deg,#6D28D9 0%,#7C3AED 45%,#9333EA 100%)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ position: "absolute", right: -40, top: -50, width: 180, height: 180, borderRadius: "50%", background: "rgba(255,255,255,0.10)" }} />
        <Box sx={{ position: "absolute", right: 70, bottom: -70, width: 140, height: 140, borderRadius: "50%", background: "rgba(255,255,255,0.07)" }} />
        <Box sx={{ position: "relative", width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, minHeight: isMobile ? 44 : 46 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1, alignSelf: "center" }}>
            <Box sx={{ width: 44, height: 44, flex: "0 0 auto", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 0, background: "rgba(255,255,255,0.18)", backdropFilter: "blur(4px)" }}>
              <DownloadIcon sx={{ fontSize: 22, display: "block" }} />
            </Box>
            <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", alignSelf: "center" }}>
              <Typography sx={{ fontWeight: 800, fontSize: isMobile ? 17 : 19, lineHeight: 1.25, m: 0 }}>
                Customer Import Template
              </Typography>
              <Typography sx={{ fontSize: 13, lineHeight: 1.35, opacity: 0.9, mt: 0.25, m: 0 }}>
                Fill 2 sheets in Excel, then import. It takes about 2 minutes.
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{
              color: "#fff",
              width: 34,
              height: 34,
              minWidth: 34,
              minHeight: 34,
              p: 0,
              flex: "0 0 auto",
              lineHeight: 0,
              background: "rgba(255,255,255,0.15)",
              "&:hover": { background: "rgba(255,255,255,0.28)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18, display: "block" }} />
          </IconButton>
        </Box>
      </Box>

      {/* ===== Body ===== */}
      <Box sx={{ flex: 1, overflowY: "auto", background: c.surface, p: isMobile ? 2 : 3 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress sx={{ color: c.brand }} />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Steps */}
            <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 1.25 }}>
              {steps.map((s, i) => (
                <Box key={s.t} sx={{ display: "flex", alignItems: "center", gap: 1.25, p: 1.5, borderRadius: "14px", background: c.card, border: `1px solid ${c.border}` }}>
                  <Box sx={{ position: "relative", width: 38, height: 38, flex: "0 0 auto", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", background: c.brandSoft, color: c.brand }}>
                    {s.icon}
                    <Box sx={{ position: "absolute", top: -6, left: -6, width: 18, height: 18, borderRadius: "50%", background: c.brand, color: "#fff", fontSize: 10.5, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", border: `2px solid ${c.card}` }}>
                      {i + 1}
                    </Box>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: c.text, lineHeight: 1.2 }}>{s.t}</Typography>
                    <Typography sx={{ fontSize: 12, color: c.sub }}>{s.d}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Sheet switcher */}
            <Box sx={{ display: "flex", p: 0.5, gap: 0.5, borderRadius: "14px", background: isDark ? "#0b1220" : "#E9EEF5", border: `1px solid ${c.border}` }}>
              {sheets.map((s, i) => {
                const active = tab === i;
                return (
                  <Box
                    key={s.key}
                    onClick={() => setTab(i)}
                    role="tab"
                    aria-selected={active}
                    sx={{
                      flex: 1,
                      cursor: "pointer",
                      userSelect: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      py: 1,
                      px: 1,
                      borderRadius: "10px",
                      transition: "all .18s",
                      background: active ? c.card : "transparent",
                      boxShadow: active ? "0 2px 8px rgba(15,23,42,0.10)" : "none",
                      color: active ? c.text : c.sub,
                      fontWeight: 700,
                      fontSize: 13,
                    }}
                  >
                    <Box sx={{ width: 22, height: 22, borderRadius: "50%", fontSize: 11.5, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", background: active ? s.accent : "transparent", color: active ? "#fff" : c.sub, border: active ? "none" : `1.5px solid ${c.sub}` }}>
                      {s.num}
                    </Box>
                    {isMobile ? s.tab.split(" ")[0] : `Sheet ${s.num} · ${s.tab}`}
                    <Box sx={{ fontSize: 11, fontWeight: 700, px: 0.8, borderRadius: "999px", background: active ? s.accentSoft : "transparent", color: active ? s.accent : c.sub }}>
                      {s.rows.length}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Sheet header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", background: sheet.accentSoft, color: sheet.accent }}>
                {sheet.icon}
              </Box>
              <Box>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: c.text, lineHeight: 1.2 }}>{sheet.title}</Typography>
                <Typography sx={{ fontSize: 12.5, color: c.sub }}>{sheet.note}</Typography>
              </Box>
            </Box>

            {/* Excel-style preview */}
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase", color: c.sub, mb: 0.75 }}>
                How your sheet will look
              </Typography>
              <Box sx={{ overflowX: "auto", borderRadius: "12px", border: `1px solid ${c.border}`, background: c.card }}>
                <Box sx={{ display: "inline-flex", minWidth: "100%", flexDirection: "column" }}>
                  {/* column letters */}
                  <Box sx={{ display: "flex", background: isDark ? "#0b1220" : "#F1F5F9", borderBottom: `1px solid ${c.border}` }}>
                    <Box sx={{ width: 34, flex: "0 0 auto" }} />
                    {sheet.rows.map((r, i) => (
                      <Box key={r.name} sx={{ flex: "1 0 128px", textAlign: "center", fontSize: 10.5, fontWeight: 700, color: c.sub, py: 0.4, borderLeft: `1px solid ${c.border}` }}>
                        {String.fromCharCode(65 + i)}
                      </Box>
                    ))}
                  </Box>
                  {/* header row */}
                  <Box sx={{ display: "flex", borderBottom: `1px solid ${c.border}` }}>
                    <Box sx={{ width: 34, flex: "0 0 auto", textAlign: "center", fontSize: 10.5, color: c.sub, py: 1, background: isDark ? "#0b1220" : "#F1F5F9" }}>1</Box>
                    {sheet.rows.map((r) => (
                      <Box key={r.name} sx={{ flex: "1 0 128px", px: 1.25, py: 1, borderLeft: `1px solid ${c.border}`, background: r.required ? sheet.accentSoft : "transparent", display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 700, color: r.required ? sheet.accent : c.text }}>
                          {r.name}
                        </Typography>
                        {r.required && <Box component="span" sx={{ color: c.reqText, fontWeight: 800, fontSize: 13 }}>*</Box>}
                        {r.name === "Contact Number" && <LinkIcon sx={{ fontSize: 13, color: sheet.accent }} />}
                      </Box>
                    ))}
                  </Box>
                  {/* example row */}
                  <Box sx={{ display: "flex" }}>
                    <Box sx={{ width: 34, flex: "0 0 auto", textAlign: "center", fontSize: 10.5, color: c.sub, py: 1, background: isDark ? "#0b1220" : "#F1F5F9" }}>2</Box>
                    {sheet.rows.map((r) => (
                      <Box key={r.name} sx={{ flex: "1 0 128px", px: 1.25, py: 1, borderLeft: `1px solid ${c.border}` }}>
                        <Typography noWrap sx={{ fontSize: 12.5, fontFamily: "ui-monospace,Menlo,monospace", color: c.sub }}>
                          {r.example}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1, fontSize: 11.5, color: c.sub }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: "3px", background: sheet.accentSoft, border: `1px solid ${sheet.accent}` }} /> Coloured header = required
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                  <LinkIcon sx={{ fontSize: 14, color: sheet.accent }} /> Contact Number links Sheet 1 and Sheet 2
                </Box>
              </Box>
            </Box>

            {/* Column guide */}
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase", color: c.sub, mb: 0.75 }}>
                Column guide
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2,1fr)", gap: 1 }}>
                {sheet.rows.map((r) => (
                  <Box key={r.name} sx={{ p: 1.4, borderRadius: "12px", background: c.card, border: `1px solid ${c.border}`, borderLeft: `4px solid ${r.required ? c.reqText : c.border}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: c.text }}>{r.name}</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box sx={{ fontSize: 10.5, fontWeight: 600, color: c.sub, px: 0.8, height: 20, display: "inline-flex", alignItems: "center", borderRadius: "6px", border: `1px solid ${c.border}` }}>
                          {r.type}
                        </Box>
                        <Pill required={r.required} />
                      </Box>
                    </Box>
                    {r.hint && <Typography sx={{ fontSize: 12, color: c.sub, mt: 0.5 }}>{r.hint}</Typography>}
                    <Typography sx={{ fontSize: 12, mt: 0.5, color: c.sub }}>
                      e.g.{" "}
                      <Box component="span" sx={{ fontFamily: "ui-monospace,Menlo,monospace", color: c.text, background: c.surface, px: 0.6, py: 0.1, borderRadius: "4px" }}>
                        {r.example}
                      </Box>
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Quantity explainer (Sheet 2 only) */}
            {sheet.key === "product" && qtyCards.length > 0 && (
              <Box sx={{ p: 2, borderRadius: "14px", background: isDark ? "rgba(22,163,74,0.10)" : "#F0FDF4", border: `1px solid ${isDark ? "rgba(22,163,74,0.35)" : "#BBF7D0"}` }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: isDark ? "#86efac" : "#166534", mb: 1.25, display: "flex", alignItems: "center", gap: 0.75 }}>
                  <CheckIcon sx={{ fontSize: 18 }} /> What do the quantity columns mean?
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : `repeat(${qtyCards.length},1fr)`, gap: 1 }}>
                  {qtyCards.map((q) => (
                    <Box key={q.name} sx={{ display: "flex", gap: 1.1, p: 1.25, borderRadius: "10px", background: c.card, border: `1px solid ${c.border}` }}>
                      <Box sx={{ width: 32, height: 32, flex: "0 0 auto", borderRadius: "9px", display: "flex", alignItems: "center", justifyContent: "center", color: q.color, background: `${q.color}1f` }}>
                        {q.icon}
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: c.text }}>{q.name}</Typography>
                        <Typography sx={{ fontSize: 12, color: c.sub }}>{q.meaning}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Typography sx={{ fontSize: 12, mt: 1.25, color: isDark ? "#bbf7d0" : "#15803D" }}>
                  Same customer, two products? Add two rows with the same Contact Number.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* ===== Footer ===== */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, px: isMobile ? 2 : 3, py: 1.75, background: c.bg, borderTop: `1px solid ${c.border}` }}>
        {!isMobile && (
          <Typography sx={{ fontSize: 12, color: c.sub }}>
            <Box component="span" sx={{ color: c.reqText, fontWeight: 800 }}>*</Box> Required. Don't rename the column headers.
          </Typography>
        )}
        <Box sx={{ display: "flex", gap: 1.25, ml: isMobile ? "auto" : 0, width: isMobile ? "100%" : "auto" }}>
          <Button
            onClick={onClose}
            sx={{ flex: isMobile ? 1 : "0 0 auto", height: 42, borderRadius: "10px", textTransform: "none", fontWeight: 600, px: 2.5, color: c.text, border: `1px solid ${c.border}`, "&:hover": { background: c.surface } }}
          >
            Close
          </Button>
          <Button
            onClick={onDownload}
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{ flex: isMobile ? 1 : "0 0 auto", height: 42, borderRadius: "10px", textTransform: "none", fontWeight: 700, px: isMobile ? 1.5 : 2.5, background: "linear-gradient(135deg,#7C3AED,#9333EA)", boxShadow: "0 8px 20px -6px rgba(124,58,237,0.6)", "&:hover": { background: "linear-gradient(135deg,#6D28D9,#7E22CE)" } }}
          >
            {isMobile ? "Download" : "Download Template"}
          </Button>
          <Button
            onClick={onImport}
            variant="contained"
            startIcon={<UploadIcon />}
            sx={{ flex: isMobile ? 1 : "0 0 auto", height: 42, borderRadius: "10px", textTransform: "none", fontWeight: 700, px: isMobile ? 1.5 : 3, background: "linear-gradient(135deg,#059669,#10B981)", boxShadow: "0 8px 20px -6px rgba(5,150,105,0.6)", "&:hover": { background: "linear-gradient(135deg,#047857,#059669)" } }}
          >
            {isMobile ? "Import" : "Import File"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
